import { resolveStoredFileUrl } from "utils/storedFileUrl";
// ModalFotos.jsx
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  Input,
  Modal,
  Tooltip
} from "@mui/material";

import { gql, useMutation } from "@apollo/client";
import addImage from "assets/images/icons/add-image.png";
import ImageCropDialog from "components/ImageCropDialog";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "80%", sm: "50%", xxl: "40%" },
  overflowY: "auto",
  p: 3,
};
const allowedFileTypes = ".jpg, .jpeg, .png, .gif";

const SAVE_FILES_PRODUCTO = gql`
  mutation addProductoFile($input: ProductoFileInput!) {
    addProductoFile(input: $input) {
      id
    }
  }
`;
const DELETE_FILE = gql`
  mutation deleteProductoFile($id: ID!) {
    deleteProductoFile(id: $id)
  }
`;

export default function ModalFotos({ open, handleClose, refetch, selectedProducto }) {
  const { handleSnackbar } = useContext(MessageManager);
  const [imagenes, setImagenes] = useState([]);
  const [imagenesData, setImagenesData] = useState([]);
  const [files, setFiles] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);

  const [cropOpen, setCropOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const [mutate, { loading: loadingMutation }] = useMutation(SAVE_FILES_PRODUCTO, {
    onCompleted: () => handleSnackbar("Cambios guardados", "success"),
    onError: () => handleSnackbar("Ha ocurrido un error, intente más tarde", "error"),
  });
  const [mutateDelete, { loading: loadingDelete }] = useMutation(DELETE_FILE);

  // Al seleccionar fichero, abrimos cropper
  const onFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPendingFile({ file, url });
      setCropOpen(true);
    }
    // reset input para poder elegir el mismo archivo luego si se cancela
    e.target.value = null;
  };

  // Cancelar recorte
  const handleCropCancel = () => {
    setPendingFile(null);
    setCropOpen(false);
  };

  // Cuando se completa el recorte
  const handleCropDone = (croppedFile) => {
    const croppedUrl = URL.createObjectURL(croppedFile);
    setFiles((prev) => [...prev, croppedFile]);
    setImagenes((prev) => [...prev, croppedUrl]);
    setPendingFile(null);
    setCropOpen(false);
  };

  const handleSave = async () => {
    // borrados
    if (deletedFiles.length > 0) {
      await Promise.all(deletedFiles.map((id) => mutateDelete({ variables: { id } })));
    }
    // nuevos
    if (files.length > 0) {
      await mutate({
        variables: {
          input: {
            file: files,
            idProducto: selectedProducto.id,
          },
        },
      });
    } else {
      handleSnackbar("Cambios guardados", "success");
    }
    refetch();
    setFiles([]);
    handleClose();
  };

  useEffect(() => {
    if (open && selectedProducto) {
      setImagenesData(selectedProducto.files || []);
    } else {
      setImagenes([]);
      setImagenesData([]);
      setDeletedFiles([]);
      setFiles([]);
    }
  }, [open, selectedProducto]);

  console.log("imagenesData", imagenesData);
  console.log("imagenes", imagenes);

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <Fade in={open}>
          <Card sx={style}>
            <SoftBox display="flex" flexDirection="column" gap={2}>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftTypography variant="h6">Fotos de {selectedProducto?.nombre}</SoftTypography>
                <Tooltip title="Cerrar" placement="top">
                  <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                    close
                  </Icon>
                </Tooltip>
              </SoftBox>
              <Grid container spacing={2}>
                {imagenes.map((src, i) => (
                  <Grid item xs={12} sm={6} md={4} key={`new-${i}`}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.02)",
                        },
                        "&:hover .deleteBtn": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Tooltip title="Eliminar" placement="top">
                        <SoftButton
                          color="primary"
                          circular
                          className="deleteBtn"
                          iconOnly
                          onClick={() => {
                            setFiles((f) => f.filter((_, idx) => idx !== i));
                            setImagenes((imgs) => imgs.filter((_, idx) => idx !== i));
                          }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            opacity: 0,
                            transition: "opacity 0.2s ease-in-out",
                            cursor: "pointer",
                          }}
                        >
                          <Icon>delete</Icon>
                        </SoftButton>
                      </Tooltip>
                      <SoftBox
                        sx={{
                          position: "relative",
                          width: "100%",
                          paddingBottom: "100%",
                          overflow: "hidden",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <SoftBox
                          component="img"
                          src={src}
                          alt={`foto-${i}`}
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center",
                            transition: "transform 0.3s ease-in-out",
                            cursor: "pointer",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                      </SoftBox>
                    </Card>
                  </Grid>
                ))}
                {imagenesData.map((fileObj, i) => (
                  <Grid item xs={12} sm={6} md={4} key={`old-${fileObj.id}`}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.02)",
                        },
                        "&:hover .deleteBtn": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Tooltip title="Eliminar" placement="top">
                        <SoftButton
                          color="primary"
                          circular
                          className="deleteBtn"
                          iconOnly
                          onClick={() => {
                            setDeletedFiles((prev) => [...prev, fileObj.id]);
                            setImagenesData((old) => old.filter((_, idx) => idx !== i));
                          }}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            opacity: 0,
                            transition: "opacity 0.2s ease-in-out",
                            cursor: "pointer",
                          }}
                        >
                          <Icon>delete</Icon>
                        </SoftButton>
                      </Tooltip>
                      <SoftBox
                        sx={{
                          position: "relative",
                          width: "100%",
                          paddingBottom: "100%",
                          overflow: "hidden",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <SoftBox
                          component="img"
                          src={resolveStoredFileUrl(fileObj.file)}
                          alt={`foto-${i}`}
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center",
                            transition: "transform 0.3s ease-in-out",
                            cursor: "pointer",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                      </SoftBox>
                    </Card>
                  </Grid>
                ))}
                <Grid item xs={12} sm={!imagenes.length && !imagenesData.length ? 12 : 6} md={!imagenes.length && !imagenesData.length ? 12 : 4}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                      "&:hover .deleteBtn": {
                        opacity: 1,
                      },
                    }}
                    onClick={() => document.getElementById("input-file").click()}
                  >
                    <SoftBox
                      sx={{
                        position: "relative",
                        width: "100%",
                        paddingBottom: "100%",
                        overflow: "hidden",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      <SoftBox
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title="Agregar foto" placement="top">
                          <SoftBox
                            component="img"
                            src={addImage}
                            sx={{
                              width: "50%",
                              height: "50%",
                              objectFit: "contain",
                              transition: "transform 0.3s ease-in-out",
                              cursor: "pointer",
                              "&:hover": {
                                transform: "scale(1.05)",
                              },
                            }}
                          />
                        </Tooltip>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Grid>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  flexDirection={{ xs: "column", sm: "row" }}
                >
                  <SoftButton
                    color="primary"
                    circular
                    onClick={handleClose}
                    fullWidth={{ xs: true, sm: false }}
                    disabled={loadingMutation || loadingDelete}
                    sx={{ mr: { sm: 2 }, mb: { xs: 2, sm: 0 } }}
                  >
                    <Icon>cancel</Icon>&nbsp;Cancelar
                  </SoftButton>
                  <SoftButton
                    color="uproGreen"
                    circular
                    onClick={handleSave}
                    fullWidth={{ xs: true, sm: false }}
                    disabled={loadingMutation || loadingDelete}
                  >
                    {loadingMutation || loadingDelete ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <Icon>save</Icon>
                    )}
                    &nbsp;Guardar
                  </SoftButton>
                </Grid>

                {/* Input file oculto */}
                <Grid sx={{ display: "none" }}>
                  <Input
                    type="file"
                    id="input-file"
                    onChange={onFileSelect}
                    inputProps={{ accept: allowedFileTypes, multiple: false }}
                  />
                </Grid>
              </Grid>
            </SoftBox>
          </Card>
        </Fade>
      </Modal>
      <ImageCropDialog
        open={cropOpen}
        src={pendingFile?.url}
        onCancel={handleCropCancel}
        onCrop={handleCropDone}
      />
    </>
  );
}

ModalFotos.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  selectedProducto: PropTypes.object,
};
